import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Phone, Mail, MapPin, Download, Wrench, Users, BadgeCheck, ShieldAlert } from "lucide-react";
import api, { API } from "../lib/api";
import { PageHeader, Loading, Empty, Avatar, TempBadge, Pill, Stamp } from "../components/kit";
import { Input, Select } from "../components/form";
import { Button } from "../components/ui/button";
import ContactForm from "../components/ContactForm";
import { useAuth } from "../context/AuthContext";
import { useVendorTaxonomy, Stars, CategoryPill } from "../components/VendorProfile";
import LastContact from "../components/LastContact";
import { CONTACT_TYPES, CLIENT_TYPES, PROFESSIONAL_TYPES, LEAD_TEMPERATURES, fullName, titleCase, isProfessional } from "../lib/constants";

const SEGMENTS = [
  { key: "all", label: "All contacts", icon: Users },
  { key: "clients", label: "Clients & leads", icon: Users },
  { key: "pros", label: "Professionals & vendors", icon: Wrench },
];

export default function Contacts() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const segment = params.get("segment") || "all";
  const category = params.get("category") || "";
  const specialty = params.get("specialty") || "";
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [temp, setTemp] = useState("");
  const [showForm, setShowForm] = useState(false);
  const nav = useNavigate();
  const tax = useVendorTaxonomy();
  const pros = segment === "pros";

  const setParam = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const setSegment = (s) => { const p = new URLSearchParams(); if (s !== "all") p.set("segment", s); setParams(p, { replace: true }); setType(""); setTemp(""); };

  const load = useCallback(async () => {
    const q = new URLSearchParams({ limit: "500" });
    if (search) q.set("search", search);
    if (type) q.set("contact_type", type);
    if (temp && !pros) q.set("lead_temperature", temp);
    if (segment === "clients") q.set("professional", "false");
    if (pros) { q.set("professional", "true"); if (category) q.set("category", category); if (specialty) q.set("specialty", specialty); }
    const { data } = await api.get(`/contacts?${q.toString()}`);
    setData(data);
  }, [search, type, temp, segment, pros, category, specialty]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = () => { window.open(`${API}/export/contacts?auth=${localStorage.getItem("gas_token")}`); };
  const typeList = pros ? PROFESSIONAL_TYPES : segment === "clients" ? CLIENT_TYPES : CONTACT_TYPES;
  const catOptions = (tax.categories || []).map((c) => ({ value: c, label: tax.counts?.[c] ? `${c} (${tax.counts[c]})` : c }));

  return (
    <div className="animate-fade-up">
      <PageHeader
        title={pros ? "Professionals & Vendors" : "Contacts"}
        subtitle={pros ? "Your trusted directory — contractors, inspectors, lenders, title, attorneys, property managers and more." : "Your complete relationship database — buyers, sellers, investors, prospects and collaborators."}
        testid="contacts-header"
        actions={
          <>
            {user?.role === "master" && <Button variant="outline" onClick={exportCsv} data-testid="export-contacts-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>}
            <Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => setShowForm(true)} data-testid="new-contact-btn"><Plus className="w-4 h-4 mr-1.5" />{pros ? "New Professional" : "New Contact"}</Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="contact-segments">
        {SEGMENTS.map((s) => (
          <button key={s.key} onClick={() => setSegment(s.key)} data-testid={`segment-${s.key}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-[background-color,color,border-color] ${segment === s.key ? "bg-navy-800 text-white border-navy-800" : "bg-white text-slate-600 border-slate-200 hover:border-gold"}`}>
            <s.icon className="w-3.5 h-3.5" />{s.label}{s.key === "pros" && tax.total ? <span className="ml-1 text-xs opacity-70">{tax.total}</span> : null}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={pros ? "Search company, trade, person, phone, city…" : "Search name, phone, email, city…"} className="pl-9" data-testid="contacts-search" />
        </div>
        <div className="w-52"><Select options={[{ value: "", label: "All types" }, ...typeList.map((t) => ({ value: t, label: titleCase(t) }))]} value={type} onChange={setType} testid="filter-type" /></div>
        {pros ? (
          <>
            <div className="w-64"><Select options={[{ value: "", label: "All categories" }, ...catOptions]} value={category} onChange={(v) => setParam("category", v)} testid="filter-category" /></div>
            <div className="w-64"><Select options={[{ value: "", label: "All trades / specialties" }, ...(tax.specialties || []).map((s) => ({ value: s, label: s }))]} value={specialty} onChange={(v) => setParam("specialty", v)} testid="filter-specialty" /></div>
          </>
        ) : (
          <div className="w-52"><Select options={[{ value: "", label: "All temperatures" }, ...LEAD_TEMPERATURES.map((t) => ({ value: t.value, label: t.label }))]} value={temp} onChange={setTemp} testid="filter-temp" /></div>
        )}
      </div>

      {!data ? <Loading /> : data.items.length === 0 ? (
        <Empty title={pros ? "No professionals match" : "No contacts found"} subtitle={pros ? "Try another category or trade, or add a new professional." : "Add your first contact or import a prospecting list."} action={<Button className="bg-navy-800 text-white" onClick={() => setShowForm(true)}>{pros ? "New Professional" : "New Contact"}</Button>} testid="contacts-empty" />
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3" data-testid="contacts-total">{data.total} {pros ? "professionals" : "contacts"}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="contacts-grid">
            {data.items.map((c) => <ContactCard key={c.id} c={c} onOpen={() => nav(`/contacts/${c.id}`)} />)}
          </div>
        </>
      )}

      <ContactForm open={showForm} onClose={() => setShowForm(false)} onSaved={load} defaultType={pros ? "contractor" : undefined} />
    </div>
  );
}

function ContactCard({ c, onOpen }) {
  const pro = isProfessional(c);
  const vp = c.vendor_profile || {};
  const name = fullName(c);
  const showCompany = c.company && c.company !== name;
  return (
    <motion.div whileHover={{ y: -3 }} onClick={onOpen} data-testid={`contact-card-${c.id}`}
      className={`bg-white rounded-xl border p-5 shadow-sm hover:shadow-md transition-[box-shadow,border-color] cursor-pointer ${pro ? "border-slate-200 hover:border-navy-800/40" : "border-slate-200 hover:border-amber-500/40"}`}>
      <div className="flex items-start gap-3">
        <Avatar contact={c} size={48} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-display font-semibold text-navy-800 truncate">{name}</p>
            {pro ? (
              <span className="flex items-center gap-1 shrink-0">
                {vp.preferred_vendor && <BadgeCheck className="w-4 h-4 text-emerald-600" title="Preferred vendor" />}
                {vp.do_not_refer && <ShieldAlert className="w-4 h-4 text-red-600" title="Do not refer" />}
                <Stars n={vp.internal_rating} />
              </span>
            ) : <TempBadge value={c.lead_temperature} />}
          </div>
          {showCompany && <p className="text-xs text-slate-500 truncate">{c.company}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {pro && vp.primary_category ? <CategoryPill>{vp.primary_category}</CategoryPill> : <Pill>{titleCase(c.contact_type || c.lead_type || "prospect")}</Pill>}
            {!pro && c.lead_source && <span className="text-xs text-slate-400">{c.lead_source}</span>}
          </div>
          {pro && (vp.specialties || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2" data-testid={`contact-specialties-${c.id}`}>
              {vp.specialties.slice(0, 4).map((s) => <span key={s} className="px-2 py-0.5 rounded-full text-[11px] bg-amber-50 text-amber-800 border border-amber-200">{s}</span>)}
              {vp.specialties.length > 4 && <span className="text-[11px] text-slate-400">+{vp.specialties.length - 4} more</span>}
            </div>
          )}
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            {c.primary_phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.primary_phone}</p>}
            {c.email && <p className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" />{c.email}</p>}
            {(c.city || c.state) && <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{[c.city, c.state].filter(Boolean).join(", ")}</p>}
          </div>
          <div className="mt-3 pt-2.5 border-t border-slate-100 space-y-1">
            <LastContact contact={c} testid={`contact-last-contact-${c.id}`} />
            <Stamp prefix="Added by" by={c.created_by_name || "System"} at={c.created_at} testid={`contact-stamp-${c.id}`} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
